import type { Subject } from './subjects.js';

/**
 * 207 (#313): the closed permission catalogue — exactly the `(subject, action, scope)` triples
 * Admin, Supplier, and Host grant TODAY per `abilities.ts`'s (soon-to-be-deleted) hardcoded
 * branches, verified line-by-line against that file (specs/207-permission-driven-roles/
 * data-model.md). No new pair invented (spec.md FR-003/FR-013).
 *
 * ONE canonical copy, here rather than in `backend/` or duplicated in test fixtures — `backend/`
 * already depends on `@carii/casl-abilities` (the only direction this repo's no-workspaces setup
 * allows), so its migration (`207-seed-role-permissions.ts`) imports this array directly, and this
 * package's own tests (`tests/fixtures/seededPermissions.ts`) do too. Two things reading from the
 * same array cannot silently disagree the way two hand-transcribed copies could (research.md R2/R6).
 *
 * 367: the Supplier ROLE is retired (its members became Hosts); no row grants it any more. The rows only it held stay,
 * with `roles: []`, for custom organisation roles. (`'Supplier'` as a SUBJECT, the company, is unchanged.)
 *
 * 'any' = unconditional. 'own_supplier' = today's `{supplier_id: user.supplier_id ?? null}`
 * condition. `roles` names which of Admin/Supplier/Host are assigned this row at seed time — a row
 * shared by more than one role is still exactly one catalogue entry.
 *
 * 303: 'own_assigned_cars' added — `{_id: {$in: assignedCarIds}}` on subject `Car` itself,
 * `{car_id: {$in: assignedCarIds}}` on everything else (`abilities.ts`'s condition-builder is
 * subject-aware for this scope). Introduced by SPLITTING existing Supplier+Host rows rather than
 * editing them in place: Supplier's own `own_supplier` row for a given (subject, action) is
 * untouched (spec FR-007), and Host gets a NEW row at `own_assigned_cars` for the same
 * (subject, action) instead of continuing to share the old one. See
 * specs/303-host-car-assignment/data-model.md for the full six-resource list.
 */
export interface RolePermissionCatalogueEntry {
  subject: Subject;
  action: string;
  scope: 'any' | 'own_supplier' | 'own_assigned_cars';
  /** 341: may be empty: a row only for custom roles, which Admin already holds through `manage`. */
  roles: Array<'Admin' | 'Host'>;
  /**
   * 419 (#1529): a row the SuperAdmin keeps: granted to no role (Admin included) and left out of 334's
   * "administrator level" comparison, so Admin stays administrator-level without holding it. Only someone who holds it
   * may give a role carrying it (333's cover).
   */
  reserved?: true;
}

export const ROLE_PERMISSION_CATALOGUE: RolePermissionCatalogueEntry[] = [
  { subject: 'Car', action: 'manage', scope: 'any', roles: ['Admin'] },
  { subject: 'Car', action: 'read', scope: 'own_supplier', roles: [] },
  { subject: 'Car', action: 'read', scope: 'own_assigned_cars', roles: ['Host'] },
  { subject: 'Car', action: 'create', scope: 'own_supplier', roles: [] },
  { subject: 'Car', action: 'update', scope: 'own_supplier', roles: [] },
  { subject: 'Car', action: 'update', scope: 'own_assigned_cars', roles: ['Host'] },
  { subject: 'Car', action: 'delete', scope: 'own_supplier', roles: [] },
  // 303 (spec FR-002): NEW grant, not migrated from a pre-existing rule (same shape as 207's own
  // `Role: read` row) — Host deliberately excluded, even though Host holds `update Car` on their
  // own assigned car (research.md, `abilities.ts`'s `assign-host` doc comment).
  { subject: 'Car', action: 'assign-host', scope: 'own_supplier', roles: [] },
  { subject: 'Car', action: 'block', scope: 'own_supplier', roles: [] },
  { subject: 'Car', action: 'unblock', scope: 'own_supplier', roles: [] },

  { subject: 'Booking', action: 'manage', scope: 'any', roles: ['Admin'] },
  { subject: 'Booking', action: 'read', scope: 'own_supplier', roles: [] },
  { subject: 'Booking', action: 'read', scope: 'own_assigned_cars', roles: ['Host'] },
  {
    subject: 'Booking',
    action: 'update-status',
    scope: 'own_supplier',
    roles: [],
  },
  {
    subject: 'Booking',
    action: 'update-status',
    scope: 'own_assigned_cars',
    roles: ['Host'],
  },
  { subject: 'Booking', action: 'cancel', scope: 'own_supplier', roles: [] },
  { subject: 'Booking', action: 'checkout', scope: 'own_supplier', roles: [] },
  { subject: 'Booking', action: 'checkout', scope: 'own_assigned_cars', roles: ['Host'] },
  { subject: 'Booking', action: 'checkin', scope: 'own_supplier', roles: [] },
  { subject: 'Booking', action: 'checkin', scope: 'own_assigned_cars', roles: ['Host'] },
  { subject: 'Booking', action: 'complete', scope: 'own_supplier', roles: [] },
  { subject: 'Booking', action: 'reschedule', scope: 'own_supplier', roles: [] },
  { subject: 'Booking', action: 'reschedule', scope: 'own_assigned_cars', roles: ['Host'] },
  // 288: own-fleet only for WHICH BOOKING may be reassigned — the replacement car itself is not
  // restricted to this supplier's fleet (enforced in route/service code, not by this condition).
  {
    subject: 'Booking',
    action: 'reassignCarOnBreakdown',
    scope: 'own_supplier',
    roles: [],
  },
  {
    subject: 'Booking',
    action: 'reassignCarOnBreakdown',
    scope: 'own_assigned_cars',
    roles: ['Host'],
  },

  { subject: 'User', action: 'manage', scope: 'any', roles: ['Admin'] },
  { subject: 'Supplier', action: 'manage', scope: 'any', roles: ['Admin'] },
  { subject: 'Host', action: 'manage', scope: 'any', roles: ['Admin'] },

  { subject: 'Location', action: 'manage', scope: 'any', roles: ['Admin'] },
  { subject: 'Location', action: 'read', scope: 'any', roles: ['Host'] },

  { subject: 'Review', action: 'manage', scope: 'any', roles: ['Admin'] },
  { subject: 'Review', action: 'read', scope: 'own_supplier', roles: [] },
  { subject: 'Review', action: 'read', scope: 'own_assigned_cars', roles: ['Host'] },
  // 341 (#934): narrow rows for custom staff roles (a CRM or finance role), seeded to no role — Admin holds each
  // through `manage`, so it stays administrator-level (334). `moderate` (see every review in the admin) is its own
  // action because everyone, guests included, already holds an unconditional `read Review` for public reviews.
  { subject: 'Review', action: 'moderate', scope: 'any', roles: [] },
  { subject: 'Review', action: 'delete', scope: 'any', roles: [] },

  { subject: 'Payment', action: 'manage', scope: 'any', roles: ['Admin'] },
  { subject: 'Payment', action: 'read', scope: 'own_supplier', roles: [] },
  // 341 (#934): a payment's staff view platform-wide, and accepting a wrong-amount payment (322) without `manage Booking`.
  { subject: 'Payment', action: 'read', scope: 'any', roles: [] },
  { subject: 'Payment', action: 'accept-mismatch', scope: 'any', roles: [] },

  { subject: 'Refund', action: 'manage', scope: 'any', roles: ['Admin'] },
  // 341 (#934): see a payment's refunds; log one. Until 341 no route checked `Refund` at all.
  { subject: 'Refund', action: 'read', scope: 'any', roles: [] },
  { subject: 'Refund', action: 'create', scope: 'any', roles: [] },
  // 342 (#933): a host's sales statement. A host is a supplier organisation: its staff read their own (Supplier,
  // own_supplier); reading every host is a narrow grant seeded to no role (Admin has it through `manage`). The
  // adjustment lines that feed it are platform staff's to keep.
  { subject: 'SalesStatement', action: 'manage', scope: 'any', roles: ['Admin'] },
  { subject: 'SalesStatement', action: 'read', scope: 'any', roles: [] },
  { subject: 'SalesStatement', action: 'read', scope: 'own_supplier', roles: [] },
  { subject: 'SalesAdjustment', action: 'manage', scope: 'any', roles: ['Admin'] },
  // 371 (#1241): customer penalties; a CRM role is given it on the Roles page.
  { subject: 'Penalty', action: 'manage', scope: 'any', roles: ['Admin'] },
  // 412 (#1505): the non-money work (see, record, edit and cancel) and the two money actions, each given per role.
  // Seeded to nobody: Admin holds every one through `manage`; migration 412 moves other roles off `manage`.
  { subject: 'Penalty', action: 'read', scope: 'any', roles: [] },
  { subject: 'Penalty', action: 'create', scope: 'any', roles: [] },
  { subject: 'Penalty', action: 'update', scope: 'any', roles: [] },
  { subject: 'Penalty', action: 'confirm-payment', scope: 'any', roles: [] },
  { subject: 'Penalty', action: 'payout', scope: 'any', roles: [] },
  { subject: 'Discount', action: 'manage', scope: 'any', roles: ['Admin'] },
  // 372 (#1240): a CRM role creates vouchers; approving is separate. Seeded to no role: Admin holds each via `manage`.
  { subject: 'Discount', action: 'read', scope: 'any', roles: [] },
  { subject: 'Discount', action: 'create', scope: 'any', roles: [] },
  { subject: 'Discount', action: 'update', scope: 'any', roles: [] },
  { subject: 'Discount', action: 'approve', scope: 'any', roles: [] },
  // 419 (#1529): immobilise or release a car through Cartrack. Reserved: the SuperAdmin's (manage all), granted to no role.
  { subject: 'CarImmobiliser', action: 'immobilise', scope: 'any', roles: [], reserved: true },
  // 420: lock or unlock a car's doors through Cartrack; reserved like the immobiliser, and separate from it.
  { subject: 'CarDoorLock', action: 'lock', scope: 'any', roles: [], reserved: true },
  // 421 (#1528): a late extension, and cancelling a past rental (410); reserved, so Admin's manage Booking misses it.
  { subject: 'PastBooking', action: 'change', scope: 'any', roles: [], reserved: true },
  { subject: 'DiscountCode', action: 'manage', scope: 'any', roles: ['Admin'] },

  { subject: 'Feature', action: 'manage', scope: 'any', roles: ['Admin'] },
  { subject: 'Feature', action: 'read', scope: 'any', roles: ['Host'] },

  { subject: 'PricingTemplate', action: 'manage', scope: 'any', roles: ['Admin'] },
  { subject: 'PricingTemplate', action: 'read', scope: 'own_supplier', roles: [] },
  { subject: 'PricingTemplate', action: 'create', scope: 'own_supplier', roles: [] },
  { subject: 'PricingTemplate', action: 'update', scope: 'own_supplier', roles: [] },
  { subject: 'PricingTemplate', action: 'delete', scope: 'own_supplier', roles: [] },

  { subject: 'GiftCertificate', action: 'manage', scope: 'any', roles: ['Admin'] },
  // 372 (#1240): issuing and approving are separate; nobody approves their own (checked by the routes).
  { subject: 'GiftCertificate', action: 'read', scope: 'any', roles: [] },
  { subject: 'GiftCertificate', action: 'create', scope: 'any', roles: [] },
  { subject: 'GiftCertificate', action: 'approve', scope: 'any', roles: [] },

  { subject: 'CarBlock', action: 'manage', scope: 'any', roles: ['Admin'] },
  { subject: 'CarBlock', action: 'create', scope: 'own_supplier', roles: [] },
  { subject: 'CarBlock', action: 'create', scope: 'own_assigned_cars', roles: ['Host'] },
  { subject: 'CarBlock', action: 'read', scope: 'own_supplier', roles: [] },
  { subject: 'CarBlock', action: 'read', scope: 'own_assigned_cars', roles: ['Host'] },
  { subject: 'CarBlock', action: 'update', scope: 'own_supplier', roles: [] },
  { subject: 'CarBlock', action: 'delete', scope: 'own_supplier', roles: [] },

  { subject: 'MaintenanceRecord', action: 'manage', scope: 'any', roles: ['Admin'] },
  { subject: 'MaintenanceRecord', action: 'create', scope: 'own_supplier', roles: [] },
  {
    subject: 'MaintenanceRecord',
    action: 'read',
    scope: 'own_supplier',
    roles: [],
  },
  {
    subject: 'MaintenanceRecord',
    action: 'read',
    scope: 'own_assigned_cars',
    roles: ['Host'],
  },
  { subject: 'MaintenanceRecord', action: 'update', scope: 'own_supplier', roles: [] },
  { subject: 'MaintenanceRecord', action: 'delete', scope: 'own_supplier', roles: [] },

  { subject: 'CarBrand', action: 'manage', scope: 'any', roles: ['Admin'] },
  { subject: 'CarBrand', action: 'read', scope: 'any', roles: ['Host'] },
  { subject: 'CarCategory', action: 'manage', scope: 'any', roles: ['Admin'] },
  { subject: 'CarCategory', action: 'read', scope: 'any', roles: ['Host'] },
  { subject: 'CarModel', action: 'manage', scope: 'any', roles: ['Admin'] },
  { subject: 'CarModel', action: 'read', scope: 'any', roles: ['Host'] },
  { subject: 'CarFeature', action: 'manage', scope: 'any', roles: ['Admin'] },
  { subject: 'CarFeature', action: 'read', scope: 'any', roles: ['Host'] },

  { subject: 'Fine', action: 'manage', scope: 'any', roles: ['Admin'] },
  { subject: 'Fine', action: 'create', scope: 'own_supplier', roles: [] },
  { subject: 'Fine', action: 'read', scope: 'own_supplier', roles: [] },
  { subject: 'Fine', action: 'update', scope: 'own_supplier', roles: [] },
  { subject: 'Fine', action: 'delete', scope: 'own_supplier', roles: [] },

  { subject: 'Membership', action: 'manage', scope: 'any', roles: ['Admin'] },
  { subject: 'Membership', action: 'read', scope: 'own_supplier', roles: ['Host'] },

  { subject: 'BookingInspection', action: 'manage', scope: 'any', roles: ['Admin'] },
  {
    subject: 'BookingInspection',
    action: 'create',
    scope: 'own_supplier',
    roles: [],
  },
  {
    subject: 'BookingInspection',
    action: 'create',
    scope: 'own_assigned_cars',
    roles: ['Host'],
  },
  {
    subject: 'BookingInspection',
    action: 'read',
    scope: 'own_supplier',
    roles: [],
  },
  {
    subject: 'BookingInspection',
    action: 'read',
    scope: 'own_assigned_cars',
    roles: ['Host'],
  },

  { subject: 'FileAsset', action: 'manage', scope: 'any', roles: ['Admin'] },
  { subject: 'FileAsset', action: 'upload', scope: 'any', roles: ['Admin', 'Host'] },

  { subject: 'Notification', action: 'manage', scope: 'any', roles: ['Admin'] },
  { subject: 'PlatformConfig', action: 'manage', scope: 'any', roles: ['Admin'] },

  // 207 (#313, research.md R4): NEW grant — the 'Role' subject and any rule referencing it did
  // not exist before this feature, so unlike every row above this one is not migrated from a
  // pre-existing rule. Deliberately `read`, never `manage` — SuperAdmin's existing unconditional
  // `manage all` is the ONLY path to `PATCH /roles/:id/permissions` (no other role gets an
  // explicit `manage Role` grant), which is the entire mechanism behind the SuperAdmin-only edit
  // gate.
  { subject: 'Role', action: 'read', scope: 'any', roles: ['Admin'] },

  // 214 (#310): the turned-away-demand report. Admin only, matching who could see the old platform
  // overview (`/admin/stats`, removed by 355) — no per-supplier scoping concept exists for a platform-wide demand count.
  { subject: 'DemandMiss', action: 'read', scope: 'any', roles: ['Admin'] },
];

/**
 * 207 (#313): the catalogue's own default assignment for a set of role names — pure lookup, no DB.
 * The ONE place "what does Admin/Supplier/Host get when nothing has ever assigned them anything
 * else" is computed, used both by `backend/src/shared/permissions.ts` (a Role document whose
 * `permission_ids` was never written — a pre-migration document, or SuperAdmin's/Customer's own
 * rows, which `defineAbility` ignores this for regardless) and by `buildAbilityFromUser`
 * (`shared/ability.ts`) when called with no `permissions` argument at all — the same "not yet
 * migrated" reading, one level higher, for the handful of callers that build an ability directly
 * without going through the request-scoped resolution path.
 */
export function catalogueDefaultPermissionsForRoleNames(names: string[]): Array<{
  subject: Subject;
  action: string;
  scope: 'any' | 'own_supplier' | 'own_assigned_cars';
}> {
  const result: Array<{
    subject: Subject;
    action: string;
    scope: 'any' | 'own_supplier' | 'own_assigned_cars';
  }> = [];
  for (const entry of ROLE_PERMISSION_CATALOGUE) {
    if (entry.roles.some((r) => names.includes(r))) {
      result.push({ subject: entry.subject, action: entry.action, scope: entry.scope });
    }
  }
  return result;
}
